import { FlowEventHandlers } from '../../../shared/flow-event';
import { LoginByOtpOneCredentialFlowEventDefinition } from './types';
import { PageName } from '@shopee-user/url';
import { appTimingUtil, monitor } from '../../../platform/monitoring';
import { BusinessPerfMetric } from '../../../platform/monitoring/performance/enums';
import {
  generateTrackingLoginView,
  TrackingDataFieldTypeLoginMethod,
  generateTrackingLoginWithSMSNextButtonClick,
  generateTrackingLoginBackButtonClick,
  generateTrackingLoginHelpButtonClick,
  generateTrackingLoginSignupButtonClick,
  generateTrackingLoginWithPasswordButtonClick,
  generateTrackingLoginSuccessAction,
  TrackingDataFieldTypeAccountType,
} from '@shopee-user/tracking-login';
import {
  generateTrackingAccountBannedContactUsButtonClick,
  generateTrackingAccountBannedOkButtonClick,
  generateTrackingAccountBannedImpression,
  TrackingDataFieldTypeOtpScenario,
} from '@shopee-user/tracking-otp-enter-verification';

export const TRACKER_NAME = 'login-by-otp-one-credential-optional';

export type LoginByOtpOneCredentialEventState = {
  authKey: string;
};

export const createInitialState = (): LoginByOtpOneCredentialEventState => {
  return {
    authKey: '',
  };
};

export function createTracker() {
  const trackPageLoad = ({ pageName }: { pageName: string }) => {
    const softNavigationDuration = appTimingUtil.getDurationFromSoftNavigationStart();
    const isSoftNavigation = softNavigationDuration > 0;

    monitor.sendDuration({
      name: BusinessPerfMetric.PageLoad,
      duration: isSoftNavigation
        ? softNavigationDuration
        : appTimingUtil.getAppAliveDuration(),
      data: {
        soft_nav: isSoftNavigation ? 'true' : 'false',
        page_name: pageName,
        // some of custom metadatas are not allowed to be accessed in Grafana because of database performance concerns
        // so here we use status to indicate the navigation type
        status: isSoftNavigation ? 'soft_nav' : 'hard_nav',
      },
    });
  };

  const handlers: FlowEventHandlers<
    LoginByOtpOneCredentialFlowEventDefinition,
    LoginByOtpOneCredentialEventState
  > = {
    onLoginPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginView({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
        trackPageLoad({
          pageName: PageName.PageLoginByOTP,
        });
      };
    },
    onLoginSubmitAuthKey: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithSMSNextButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
      };
    },
    onLoginBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginBackButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
      };
    },
    onLoginHelpClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginHelpButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
      };
    },
    onLoginLoginWithPasswordClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithPasswordButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
      };
    },
    onLoginSignupClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSignupButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Phone,
          })
        );
      };
    },
    onLoginAccountBannedInit: () => {
      return ({ tools }) => {
        tools.sendImpressionEvent(
          generateTrackingAccountBannedImpression({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginAccountBannedOkClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedOkButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginAccountBannedContactUsClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedContactUsButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginLoginSuccess: ({
      accountType,
      loginMethod,
    }: {
      accountType: TrackingDataFieldTypeAccountType;
      loginMethod: TrackingDataFieldTypeLoginMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSuccessAction({
            loginMethod,
            accountType,
          })
        );
      };
    },
  };
  return {
    name: TRACKER_NAME,
    handlers,
  };
}
